'use client';

import { useT } from '@/hooks/useT';
import { usePriceConvert } from '@/lib/PriceContext';
import { CapabilityList } from './CapabilityList';
import type { CapabilityItem, LangSupport } from '@/types/pdp';

interface Props {
  capabilities: CapabilityItem[];
  developer?: string | null;
  publisher?: string | null;
  releaseYear?: number | null;
  releaseDateLabel?: string | null;
  isComingSoon: boolean;
  isPlaceholderDate: boolean;
  maxInstallSize?: number | null;
  languageSupport: LangSupport[];
}

function fmtBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  return `${(bytes / 1_048_576).toFixed(0)} MB`;
}

const GLASS_SM: React.CSSProperties = {
  borderRadius: 16, border: '1px solid var(--v4-border)',
  background: 'var(--v4-panel)', backdropFilter: 'blur(20px)', padding: 20,
};

function SecBar() {
  return <span style={{ width: 3, height: 16, background: 'var(--gs-green)', borderRadius: 2, display: 'block', flexShrink: 0 }} />;
}

const SecTitleStyle: React.CSSProperties = {
  fontSize: 15, fontWeight: 800, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
};

export function PdpMorePanel({ capabilities, developer, publisher, releaseYear, releaseDateLabel, isComingSoon, isPlaceholderDate, maxInstallSize, languageSupport }: Props) {
  const t = useT();
  const { lang } = usePriceConvert();

  const techRows = [
    { key: 'dev',  label: t('pdp_tech_developer'), value: developer },
    { key: 'pub',  label: t('pdp_tech_publisher'),  value: publisher },
    { key: 'rel',  label: isComingSoon ? t('pdp_tech_release_date') : t('pdp_tech_release_year'), value: isPlaceholderDate ? 'Game Preview' : isComingSoon ? releaseDateLabel : releaseYear?.toString() },
    { key: 'size', label: t('pdp_tech_size'),        value: maxInstallSize ? fmtBytes(maxInstallSize) : null },
  ].filter(r => r.value);

  const activeLangs = languageSupport.filter(l => l.is_audio_supported || l.are_subtitles_supported || l.is_interface_supported);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Capabilities */}
      {capabilities.length > 0 && (
        <div className="pdp-glass" style={GLASS_SM}>
          <div style={SecTitleStyle}><SecBar />{t('pdp_capabilities')}</div>
          <CapabilityList capabilities={capabilities} />
        </div>
      )}

      {/* Tech info */}
      {techRows.length > 0 && (
        <div className="pdp-glass" style={GLASS_SM}>
          <div style={SecTitleStyle}><SecBar />{t('pdp_tech_info')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {techRows.map(({ key, label, value }) => (
              <div key={key} className="pdp-tech-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, borderBottom: '1px solid var(--v4-border)', paddingBottom: 8 }}>
                <span className="pdp-tech-label" style={{ color: 'var(--v4-fg-3)' }}>{label}</span>
                <span className="pdp-tech-value" style={{ color: 'var(--v4-fg-2)', fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Language support */}
      {activeLangs.length > 0 && (
        <div className="pdp-glass" style={GLASS_SM}>
          <div style={{ ...SecTitleStyle, marginBottom: 10 }}>
            <SecBar />
            <span>{t('pdp_lang_support')}</span>
            <span className="pdp-lang-count" style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: 'var(--v4-fg-4)' }}>
              {activeLangs.length} {t('pdp_lang_count')}
            </span>
          </div>
          <div className="pdp-lang-head-row" style={{ display: 'grid', gridTemplateColumns: '1fr repeat(3,52px)', gap: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--v4-fg-4)', padding: '0 8px', marginBottom: 6 }}>
            <span>{t('pdp_lang_col_lang')}</span>
            <span style={{ textAlign: 'center' }}>{t('pdp_lang_col_audio')}</span>
            <span style={{ textAlign: 'center' }}>{t('pdp_lang_col_subs')}</span>
            <span style={{ textAlign: 'center' }}>UI</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {activeLangs.map(ls => (
              <div key={ls.language_code} className="pdp-lang-row" style={{ display: 'grid', gridTemplateColumns: '1fr repeat(3,52px)', gap: 8, alignItems: 'center', padding: '5px 8px', borderRadius: 8, background: 'var(--v4-panel)', border: '1px solid var(--v4-border)', fontSize: 12 }}>
                <span className="pdp-lang-name" style={{ color: 'var(--v4-fg-2)', fontWeight: 600 }}>{ls.language_display_name ?? ls.language_code}</span>
                {[ls.is_audio_supported, ls.are_subtitles_supported, ls.is_interface_supported].map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'center' }}>
                    <span style={{ fontSize: 12, color: v ? 'var(--gs-green)' : 'var(--v4-fg-4)' }}>{v ? '✓' : '—'}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
